import { macros, type Macro } from 'Shared/common/posterMacros';

/*
	Parsing/evaluation of TextFieldObject template expressions - the code inside the
	"{...}" of a template. The macros themselves live in posterMacros.ts; this file
	only knows how to read an expression and dispatch to them.
*/

const MACRO_CALL = /^([a-zA-Z_$][\w$]*)\s*\((.*)\)$/s;
const NUMBER     = /^-?\d+(\.\d+)?$/;

function resolveField(record: unknown, path: string): unknown {
	return path.split('.').reduce((cur: unknown, key) => (cur as Record<string, unknown>)?.[key], record);
}

/* Split a macro argument list on top-level commas, respecting quotes and nested parens. */
function splitArgs(src: string): string[] {
	const args: string[] = [];
	let depth = 0, quote = '', cur = '';
	for (const c of src) {
		if (quote)                         { cur += c; if (c === quote) quote = ''; }
		else if (c === '"' || c === "'")   { quote = c; cur += c; }
		else if (c === '(')                { depth++; cur += c; }
		else if (c === ')')                { depth--; cur += c; }
		else if (c === ',' && depth === 0) { args.push(cur); cur = ''; }
		else                               cur += c;
	}
	args.push(cur);
	return args.map(a => a.trim()).filter(a => a !== '');
}

/*
	Evaluate the contents of a single "{...}" template expression. Supports:
	  - field paths against the record:  startTime, artist.name
	  - string / number literals:        'Live', "tonight", 42
	  - macro calls (possibly nested):   simpleDate(startTime), weekday(day)
	Returns undefined when a field path or macro is unresolved, so the caller can
	leave the original "{...}" text in place.
*/
export function evaluateExpression(expr: string, record: unknown): unknown {
	const trimmed = expr.trim();

	if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
	    (trimmed.startsWith("'") && trimmed.endsWith("'")))
		return trimmed.slice(1, -1);

	if (NUMBER.test(trimmed))
		return Number(trimmed);

	const call = MACRO_CALL.exec(trimmed);
	if (call) {
		const [, name, argsSrc] = call;
		const fn = (macros as unknown as Record<string, Macro>)[name];
		if (!fn) return undefined;
		const args = splitArgs(argsSrc).map(a => evaluateExpression(a, record));
		return fn(...args);
	}

	return resolveField(record, trimmed);
}
